// Copyright (c) 2026 Michael F. Kamprath
//
// scene.js -- PixiJS scene renderer for Ewe Can Count.
//
// Phase 1b: loads a theme's background, sky, overlay, and static sprites,
// composes them in the correct z-order for the depth illusion, and places
// object sprites at randomly-selected positions from the theme's position
// list.
//
// The stage hierarchy (bottom to top):
//   SkyLayer → CloudContainer → BackgroundLayer → ObjectContainer
//     → ChoicePlaqueContainer → BeeContainer → OverlayLayer →
//     LightningFlashContainer
//
// ChoicePlaqueContainer sits above ObjectContainer (so choice plaques
// render on top of countable sprites) and below OverlayLayer (so the
// barn/fence/etc. overlay still frames the scene above everything).
// The container is empty in Learn mode; the quiz-plaque manager fills
// it in Quiz mode (see QUIZ-MODE-LEGACY-SPEC §10.7).
//
// Themes with an overlay image (cows, chicks, horses) sandwich the objects
// between background and overlay so they appear to emerge from a building.
//
// Two layout variants are supported (see canvas.js):
//   standard (4:3) -- uses iPad backgrounds and positions
//   wide     (16:9) -- uses iPhone 5 backgrounds and positions
//
// The variant is chosen at scene-build time based on the viewport aspect
// ratio.  The canvas design size is then locked to that variant so that
// window resizes don't mismatch the scene content.

import { Container, Sprite, Assets, Rectangle } from 'pixi.js';
import { createAlphaMaskFromUrl } from './alpha-mask.js';

// Alpha threshold for countable-sprite hit-testing.  Same scale as
// cloud-spawner.js (0..255); a pixel below this counts as "empty"
// so taps on transparent space inside the sprite's rectangle -- most
// visibly the dead air above the flower head where the number plaque
// reveals -- don't trigger the count reaction.
const OBJECT_ALPHA_MIN = 8;

// Each entrance "object" is a Container whose position is the object's
// top-left in design space.  Inside the container sits a Sprite with
// anchor (0.5, 0.5) positioned at (w/2, h/2) in container-local space,
// so the inner sprite's visual center coincides with the container's
// own (w/2, h/2).  This lets the animation engine flip, rotate, and
// swap textures on the inner sprite without disturbing the container's
// top-left position -- a flipped or rotated sprite stays visually in
// the same bounding box as the unflipped/unrotated one.
//
// Entrance code reads and writes the container's (x, y); it only
// touches the inner sprite for scale (flip), rotation, and texture.
import { getLayoutVariant, getDesignSize } from './canvas.js';

/**
 * Pick `count` unique random indices from an array of length `total`,
 * using Fisher-Yates partial shuffle.
 *
 * @param {number} total
 * @param {number} count
 * @returns {number[]}
 */
function pickRandomIndices(total, count) {
  const indices = Array.from({ length: total }, (_, i) => i);
  const n = Math.min(count, total);
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(Math.random() * (total - i));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices.slice(0, n);
}

/**
 * Load a theme's data from the themes.json manifest.
 *
 * @param {string} themeId  One of: sheep, cows, chicks, flowers, horses, apples
 * @returns {Promise<Object>} Theme data with sprites, spriteSize, standard, wide, etc.
 */
export async function loadThemeData(themeId) {
  const resp = await fetch('/themes.json');
  if (!resp.ok) throw new Error(`Failed to load themes.json: ${resp.status}`);
  const themes = await resp.json();
  const theme = themes[themeId];
  if (!theme) throw new Error(`Unknown theme: ${themeId}`);
  return theme;
}

/**
 * Build a static scene on the given PixiJS stage.
 *
 * The layout variant (standard vs wide) is determined by the current
 * viewport aspect ratio.  This selects the matching background images
 * and position list from the theme data, then locks the canvas design
 * resolution so that subsequent window resizes keep everything aligned.
 *
 * @param {import('pixi.js').Container} stage  The PixiJS app.stage
 * @param {Object} theme  Theme data from loadThemeData()
 * @param {number} objectCount  How many objects to place (clamped to available positions)
 * @param {{ lockDesignSize?: (w:number,h:number)=>void, unlockDesignSize?: ()=>void }} [canvasHandle]
 *   Pass the canvas object returned by createCanvas() so the scene can lock/unlock the design size.
 * @param {number[]} [positionIndices]  Optional pre-selected position indices.
 *   When rebuilding a scene after an aspect-ratio change, pass the previous
 *   scene's `selectedIndices` so objects keep the same slots.
 * @returns {Promise<{ sceneRoot: Container, objects: Sprite[], variant: string, selectedIndices: number[], destroy: () => void }>}
 */
export async function buildScene(stage, theme, objectCount, canvasHandle, positionIndices) {
  const variant = getLayoutVariant();
  const { width: designW, height: designH } = getDesignSize(variant);
  const layout = theme[variant];

  // Lock the canvas to this variant's design resolution so resize()
  // doesn't switch out from under us.
  if (canvasHandle?.lockDesignSize) {
    canvasHandle.lockDesignSize(designW, designH);
  }

  const sceneRoot = new Container();
  stage.addChild(sceneRoot);

  // --- Preload all required textures -----------------------------------------
  // IMPORTANT: PIXI's Assets module is a process-wide singleton.  If we
  // reuse short aliases like 'sky' / 'background' / 'staticSprite' across
  // scenes, the SECOND `Assets.add` for a given alias is silently ignored
  // and `Assets.load` returns the OLD cached texture.  When the variant
  // changes (standard <-> wide), this would leave the previous variant's
  // background stretched onto the new design size.
  //
  // Using the URL itself as the alias makes each variant's image get its
  // own cache entry, while still letting unchanged textures (e.g. the
  // shared static sprite art) hit the cache on revisit.
  const skyUrl = `/${layout.sky}`;
  const backgroundUrl = `/${layout.background}`;
  const staticSpriteUrl = `/${theme.sprites.static}`;
  const overlayUrl = layout.overlay ? `/${layout.overlay}` : null;
  // Optional blossom overlay sprite, one per object.  Currently used
  // by the apple theme so each apple can have a flower blooming on a
  // SEPARATE sprite behind it (parallel with the apple's own grow
  // animation).  Other themes leave `sprites.blossom` unset.
  const blossomUrl = theme.sprites?.blossom ? `/${theme.sprites.blossom}` : null;

  const texturesToLoad = [
    { alias: skyUrl,          src: skyUrl,          key: 'sky' },
    { alias: backgroundUrl,   src: backgroundUrl,   key: 'background' },
    { alias: staticSpriteUrl, src: staticSpriteUrl, key: 'staticSprite' },
  ];
  if (overlayUrl) {
    texturesToLoad.push({ alias: overlayUrl, src: overlayUrl, key: 'overlay' });
  }
  if (blossomUrl) {
    texturesToLoad.push({ alias: blossomUrl, src: blossomUrl, key: 'blossomSprite' });
  }

  for (const t of texturesToLoad) {
    Assets.add({ alias: t.alias, src: t.src });
  }
  const loaded = await Assets.load(texturesToLoad.map((t) => t.alias));
  const textures = {};
  for (const t of texturesToLoad) {
    textures[t.key] = loaded[t.alias];
  }

  // Pixel-perfect hit mask for the static sprite.  Used by the
  // objContainer hitArea below so taps on the transparent part of
  // the sprite's bounding rectangle (e.g. the empty air above the
  // flower head where the number plaque reveals) don't register as
  // taps on the countable object.  Null return is tolerated --
  // callers fall back to the original Rectangle hit area so the
  // sprite stays fully tappable instead of becoming silently dead.
  const staticAlphaMask = await createAlphaMaskFromUrl(staticSpriteUrl);

  // --- Layer 1: Sky ----------------------------------------------------------
  const skySprite = new Sprite(textures.sky);
  skySprite.width = designW;
  skySprite.height = designH;
  skySprite.eventMode = 'none';
  sceneRoot.addChild(skySprite);

  // --- Layer 2: Cloud container ---------------------------------------------
  // Sits between the sky and the backdrop so clouds are occluded by any
  // opaque pixels in the backdrop image (trees, hills, barn base).  This
  // mirrors the legacy BackgroundView's three-image stack in Objective-C
  // (_skyView < _backgroundView < _overlayView) where clouds are
  // addSubview'd into _skyView via addViewToSky:, leaving them ABOVE the
  // pure sky but BELOW the backdrop image.  Populated by cloud-spawner.js.
  const cloudContainer = new Container();
  sceneRoot.addChild(cloudContainer);

  // --- Layer 3: Background ---------------------------------------------------
  const bgSprite = new Sprite(textures.background);
  bgSprite.width = designW;
  bgSprite.height = designH;
  bgSprite.eventMode = 'none';
  sceneRoot.addChild(bgSprite);

  // --- Layer 4: Object container ---------------------------------------------
  const objectContainer = new Container();
  objectContainer.interactiveChildren = true;
  sceneRoot.addChild(objectContainer);

  // --- Layer 5: Choice plaque container (quiz mode) --------------------------
  // Always present in the display list so the z-order is stable regardless
  // of which mode is active; the quiz-plaque manager adds plaques into it
  // lazily in Quiz rounds.  Learn mode leaves it empty.
  const choicePlaqueContainer = new Container();
  choicePlaqueContainer.interactiveChildren = true;
  sceneRoot.addChild(choicePlaqueContainer);

  // --- Layer 6a: Bee container (flowers-only ambient side-game) -------------
  // Always present in the display list so the z-order is stable
  // regardless of theme; the bee-spawner populates it only on
  // flowers.  Sits ABOVE objects + choice plaques so the bee
  // renders in front when dipping to visit a countable flower,
  // but BELOW the overlay layer (legacy has overlay=null on
  // flowers so this only matters architecturally -- themes that
  // gain an overlay later still get correct occlusion).
  const beeContainer = new Container();
  beeContainer.interactiveChildren = true;
  sceneRoot.addChild(beeContainer);

  // --- Layer 6: Overlay (depth illusion) -------------------------------------
  let overlaySprite = null;
  if (textures.overlay) {
    overlaySprite = new Sprite(textures.overlay);
    overlaySprite.width = designW;
    overlaySprite.height = designH;
    // The overlay covers the full scene and sits on top of the object
    // container in the display list.  Without 'none', PixiJS v8's hit
    // test would check the overlay first (topmost child) and its
    // bounding box covers all objects, blocking pointer events from
    // reaching the countable sprites underneath.
    overlaySprite.eventMode = 'none';
    sceneRoot.addChild(overlaySprite);
  }

  // --- Layer 7: Lightning-flash container (above everything) -----------------
  // Host for the whole-screen white flash graphic driven by
  // screen-flash.js + cloud-spawner.js on a cloud tap.  The legacy
  // iOS code puts `_lightningView` as a subview of `_overlayView`,
  // i.e. ABOVE the overlay -- a real thunderbolt flash occludes
  // trees, barns, fences and everything else.  Event-mode 'none' so
  // the empty container never intercepts taps when no flash is
  // active and so that the flash graphic, once added, doesn't
  // swallow pointer events mid-animation (the white rect covers the
  // entire design area).
  const lightningFlashContainer = new Container();
  lightningFlashContainer.eventMode = 'none';
  sceneRoot.addChild(lightningFlashContainer);

  // --- Place object sprites --------------------------------------------------
  // The sprite dimensions in themes.json are @2x pixel sizes.  The standard
  // design is 1024x768 (1x of the @2x~ipad assets) so we halve them.  The
  // wide design is 568x320 (1x of the -568h@2x assets) so we also halve.
  const spriteW = theme.spriteSize.w / 2;
  const spriteH = theme.spriteSize.h / 2;

  const positions = layout.positions;
  const maxObjects = Math.min(objectCount, positions.length);

  // If pre-selected indices were provided (e.g. aspect-ratio rebuild),
  // reuse them -- filtering out any that exceed the new variant's position
  // count.  Only pick fresh random indices if none were supplied.
  let selectedIndices;
  if (positionIndices) {
    selectedIndices = positionIndices.filter((i) => i < positions.length);
    // If the new variant has fewer positions than we need, top up randomly
    // from the unused slots.
    if (selectedIndices.length < maxObjects) {
      const used = new Set(selectedIndices);
      const available = [];
      for (let i = 0; i < positions.length; i++) {
        if (!used.has(i)) available.push(i);
      }
      // Shuffle the available pool
      for (let i = available.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [available[i], available[j]] = [available[j], available[i]];
      }
      selectedIndices = selectedIndices.concat(
        available.slice(0, maxObjects - selectedIndices.length),
      );
    }
  } else {
    selectedIndices = pickRandomIndices(positions.length, maxObjects);
  }

  const objects = [];
  for (const idx of selectedIndices) {
    const [px, py] = positions[idx];
    const finalX = px * designW;
    const finalY = py * designH;

    // Outer container -- positioned at the object's logical top-left.
    const objContainer = new Container();
    objContainer.x = finalX;
    objContainer.y = finalY;
    objContainer._finalX = finalX;
    objContainer._finalY = finalY;
    objContainer._staticTexture = textures.staticSprite;
    objContainer._spriteW = spriteW;
    objContainer._spriteH = spriteH;

    // Inner sprite -- anchor (0.5, 0.5) so flip/rotate happen around
    // its visual center.  Positioned at (w/2, h/2) in container space
    // so the sprite's visual top-left aligns with the container origin.
    const innerSprite = new Sprite(textures.staticSprite);
    innerSprite.anchor.set(0.5, 0.5);
    innerSprite.width = spriteW;
    innerSprite.height = spriteH;
    innerSprite.x = spriteW / 2;
    innerSprite.y = spriteH / 2;
    objContainer._inner = innerSprite;
    objContainer.addChild(innerSprite);

    // Remember the base scale PIXI computed when we set inner.width/
    // inner.height.  Cycle-scale keyframes multiply against this base
    // so the resting size stays correct across texture swaps (e.g.
    // apple grow animation swapping between 4 face textures that
    // share apple_static's native 159x171 dimensions, or the apple
    // blossom swapping to a 269x248 flower texture where PIXI's
    // scale-based sizing naturally produces the correct larger
    // display size via the same scale factor).
    objContainer._baseScaleX = innerSprite.scale.x;
    objContainer._baseScaleY = innerSprite.scale.y;

    // Enable tap/pointer interaction on this object.  PixiJS v8 uses
    // eventMode 'static' for elements that receive events but don't
    // move under the pointer (the objects are at rest post-entrance).
    objContainer.eventMode = 'static';
    objContainer.cursor = 'pointer';
    // Pixel-perfect hit area: a tap must land on a non-transparent
    // pixel of the static sprite's silhouette.  Falls back to a full
    // rectangle when the mask couldn't load (jsdom / cross-origin /
    // etc.) so the sprite stays tappable in degraded environments.
    // The inner sprite is drawn from container-local (0,0) to
    // (spriteW, spriteH), so container-local x/y map directly to
    // the texture's normalized UVs.
    if (staticAlphaMask) {
      objContainer.hitArea = {
        contains(x, y) {
          if (x < 0 || y < 0 || x > spriteW || y > spriteH) return false;
          const u = x / spriteW;
          const v = y / spriteH;
          return staticAlphaMask.getAlphaAt(u, v) >= OBJECT_ALPHA_MIN;
        },
      };
    } else {
      objContainer.hitArea = new Rectangle(0, 0, spriteW, spriteH);
    }
    // Per-sprite state flag: true while a touch reaction is playing.
    // Prevents double-tap stacking and tells idle to stay paused.
    objContainer._reacting = false;

    // Optional blossom overlay sub-container.  When present
    // (theme.sprites.blossom configured, currently apples only), it
    // sits as a SIBLING to the inner sprite inside the same object
    // container so the entrance engine can animate it in PARALLEL
    // with the main sprite.  The overlay is added BEFORE the inner
    // sprite so it renders behind (lower z-index within the
    // container), matching the legacy "flower behind apple" layer
    // order.  Starts invisible (alpha 0); the entrance engine raises
    // alpha when the blossom cycle plays.
    if (textures.blossomSprite) {
      const blossomInner = new Sprite(textures.blossomSprite);
      blossomInner.anchor.set(0.5, 0.5);
      // Match the main sprite's display box so the blossom sits in
      // the same logical bounding rectangle.  The cycle's scale
      // keyframes will size it relative to this base.
      blossomInner.width = spriteW;
      blossomInner.height = spriteH;
      blossomInner.x = spriteW / 2;
      blossomInner.y = spriteH / 2;
      blossomInner.alpha = 0;

      // Wrap in a sub-container so the entrance engine can treat it
      // as a first-class "sprite" with the same duck-typed fields
      // (_inner, _baseScaleX/Y, _staticTexture, _spriteW/H).
      const blossomContainer = new Container();
      blossomContainer._inner = blossomInner;
      blossomContainer._baseScaleX = blossomInner.scale.x;
      blossomContainer._baseScaleY = blossomInner.scale.y;
      blossomContainer._staticTexture = textures.blossomSprite;
      blossomContainer._spriteW = spriteW;
      blossomContainer._spriteH = spriteH;
      blossomContainer.addChild(blossomInner);
      // Parent container's local origin matches the world top-left of
      // the main container's position, so the blossom container at
      // local (0, 0) aligns visually with the main sprite.
      blossomContainer.x = 0;
      blossomContainer.y = 0;

      // Add BEFORE the main inner sprite so it renders behind it in
      // PIXI's z-order (last-added is on top).
      objContainer.addChildAt(blossomContainer, 0);
      objContainer._blossomOverlay = blossomContainer;
    }

    objectContainer.addChild(objContainer);
    objects.push(objContainer);
  }

  return {
    sceneRoot,
    objectContainer,
    cloudContainer,
    choicePlaqueContainer,
    beeContainer,
    lightningFlashContainer,
    objects,
    variant,
    selectedIndices,
    designW,
    designH,
    destroy() {
      stage.removeChild(sceneRoot);
      sceneRoot.destroy({ children: true });
      // Release the design-size lock so the next scene can pick fresh.
      if (canvasHandle?.unlockDesignSize) {
        canvasHandle.unlockDesignSize();
      }
    },
  };
}
